<template>
  <div class="calculation-page">
    <div class="page-header">
      <h2 class="page-title">配额测算</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">核算年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="选择年度"
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">行业类型</span>
        <el-select v-model="industry" size="small" style="width: 120px">
          <el-option v-for="item in industries" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">产品产量</span>
        <el-input-number v-model="output" size="small" :min="100000" :step="100000" />
      </div>
      <div class="filter-item">
        <span class="filter-label">基准值</span>
        <el-input-number v-model="baseline" size="small" :min="0.1" :max="5" :step="0.05" :precision="2" />
        <span class="filter-unit">tCO2/单位</span>
      </div>
      <div class="filter-item">
        <span class="filter-label">下降系数</span>
        <el-input-number v-model="reductionRate" size="small" :min="0" :max="20" :step="0.5" :precision="1" />
        <span class="filter-unit">%</span>
      </div>
      <el-button type="primary" size="small" @click="loadData">开始测算</el-button>
    </filter-bar>

    <div class="stat-cards-row">
      <stat-card
        title="预测配额"
        :today="summary.predictedQuota.today"
        :annual="summary.predictedQuota.annual"
        :unit="summary.predictedQuota.unit"
        :annual-unit="summary.predictedQuota.unit"
        icon="el-icon-data-analysis"
        color="#409EFF"
      />
      <stat-card
        title="预测排放量"
        :today="summary.predictedEmission.today"
        :annual="summary.predictedEmission.annual"
        :unit="summary.predictedEmission.unit"
        :annual-unit="summary.predictedEmission.unit"
        icon="el-icon-s-data"
        color="#E6A23C"
      />
      <stat-card
        title="预计盈缺"
        :today="summary.gap.today"
        :annual="summary.gap.annual"
        :unit="summary.gap.unit"
        :annual-unit="summary.gap.unit"
        icon="el-icon-warning-outline"
        :color="summary.gap.today >= 0 ? '#67C23A' : '#F56C6C'"
      />
      <stat-card
        title="履约成本估算"
        :today="summary.complianceCost.today"
        :annual="summary.complianceCost.annual"
        :unit="summary.complianceCost.unit"
        :annual-unit="summary.complianceCost.unit"
        icon="el-icon-money"
        color="#1a3a5c"
      />
    </div>

    <chart-card title="月度配额与排放预测" class="chart-section">
      <base-chart :option="chartOption" />
    </chart-card>

    <div class="table-section">
      <el-table :data="monthlyDetails" border stripe size="small">
        <el-table-column prop="month" label="月份" width="120" />
        <el-table-column prop="predicted" label="预测配额 (tCO2)" min-width="150" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.predicted, 0) }}</template>
        </el-table-column>
        <el-table-column prop="actual" label="预测排放 (tCO2)" min-width="150" align="right">
          <template slot-scope="{ row }">
            {{ row.actual != null ? formatNumber(row.actual, 0) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="diff" label="盈缺值 (tCO2)" min-width="140" align="right">
          <template slot-scope="{ row }">
            <span
              v-if="row.diff !== null && row.diff !== ''"
              :class="row.diff < 0 ? 'text-danger' : ''"
            >
              {{ formatNumber(row.diff, 0) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="isForecast" label="数据类型" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.isForecast ? 'warning' : ''" size="mini">
              {{ row.isForecast ? '预测' : '实际' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getCalculationData } from '@/api/modules/carbonAsset'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonAssetCalculation',
  components: { FilterBar, StatCard, ChartCard, BaseChart },
  data() {
    return {
      year: '2025',
      industry: '电力',
      output: 1000000,
      baseline: 0.85,
      reductionRate: 2,
      industries: [],
      summary: {
        predictedQuota: { today: 0, annual: 0, unit: 'tCO2' },
        predictedEmission: { today: 0, annual: 0, unit: 'tCO2' },
        gap: { today: 0, annual: 0, unit: 'tCO2' },
        complianceCost: { today: 0, annual: 0, unit: '万元' }
      },
      monthlyDetails: [],
      forecastStartIndex: 5,
      chartOption: {}
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getCalculationData({
        year: this.year,
        industry: this.industry,
        output: this.output,
        baseline: this.baseline,
        reductionRate: this.reductionRate
      })
      const data = res.data
      this.industries = data.industries
      this.summary = data.summary
      this.monthlyDetails = data.monthlyDetails
      this.forecastStartIndex = data.forecastStartIndex
      this.buildChartOption()
    },
    buildChartOption() {
      const months = this.monthlyDetails.map(item => item.month)
      const predicted = this.monthlyDetails.map(item => item.predicted)
      const actual = this.monthlyDetails.map(item => item.actual)
      const splitIndex = this.forecastStartIndex

      const predictedHistorical = predicted.map((v, i) => (i < splitIndex ? v : null))
      const predictedForecast = predicted.map((v, i) => (i >= splitIndex - 1 ? v : null))

      this.chartOption = {
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['预测配额 (tCO2)', '预测排放 (tCO2)'],
          bottom: 0
        },
        grid: { left: 60, right: 30, top: 30, bottom: 50 },
        xAxis: {
          type: 'category',
          data: months,
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          name: 'tCO2',
          axisLabel: {
            formatter: val => (val / 10000) + '万'
          }
        },
        series: [
          {
            name: '预测配额 (tCO2)',
            type: 'line',
            data: predictedHistorical,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#409EFF', width: 2 },
            itemStyle: { color: '#409EFF' },
            areaStyle: { color: 'rgba(103, 194, 58, 0.15)' }
          },
          {
            name: '预测配额 (tCO2)',
            type: 'line',
            data: predictedForecast,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#409EFF', width: 2, type: 'dashed' },
            itemStyle: { color: '#409EFF' },
            showSymbol: true
          },
          {
            name: '预测排放 (tCO2)',
            type: 'line',
            data: actual,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#E6A23C', width: 2 },
            itemStyle: { color: '#E6A23C' },
            areaStyle: { color: 'rgba(245, 108, 108, 0.2)' }
          }
        ]
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-section {
  height: 420px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  overflow-x: auto;
}

.filter-unit {
  font-size: 13px;
  color: @text-secondary;
  margin-left: 4px;
}
</style>
